import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Menu, X, Search, Loader2 } from 'lucide-react';
import CartIndicator from './CartIndicator';
import LoginIndicator from './LoginIndicator';
import CategoryDropdown from './CategoryDropdown';
import NotificationDropdown from '../../shared/NotificationDropdown';
import { RootState } from '../../../store/store';
import { useSearchProductsQuery } from '../../../store/slices/storefrontProductsSlice';
import { useDebounce } from '../../../hooks/useDebounce';
import { getImageUrl } from '../../../config/api';

interface Subcategory {
  id: string;
  name: string;
  categoryId: string;
}

interface Category {
  id: string;
  name: string;
  subcategories: Subcategory[];
}

interface NavbarProps {
  categories: Category[];
  cartItemCount: number;
}

const DROPDOWN_PAGE_SIZE = 5;

interface SearchDropdownProps {
  query: string;
  onSelect: (productId: string) => void;
  onViewAll: () => void;
  visible: boolean;
}

function SearchDropdown({ query, onSelect, onViewAll, visible }: SearchDropdownProps) {
  const debouncedQuery = useDebounce(query, 300);
  const [page, setPage] = useState(1);

  // Reset page when query changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const { data, isLoading, isFetching } = useSearchProductsQuery(
    { searchTerm: debouncedQuery, pageNumber: 1, pageSize: page * DROPDOWN_PAGE_SIZE },
    { skip: !debouncedQuery || !visible }
  );

  const products = data?.data || [];
  const totalCount = data?.totalCount || 0;
  const hasMore = products.length < totalCount;
  const showLoading = isLoading || (isFetching && products.length === 0);

  if (!visible || !debouncedQuery) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-neutral-200 z-[1100] max-h-[70vh] overflow-y-auto">
      {showLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-brand-primary" />
          <span className="ml-2 text-sm text-neutral-500">Searching...</span>
        </div>
      )}

      {!showLoading && products.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-neutral-500">
          No products found for "{debouncedQuery}"
        </div>
      )}

      {!showLoading && products.length > 0 && (
        <>
          <div className="px-3 py-2 text-xs text-neutral-400 border-b border-neutral-100">
            {totalCount} {totalCount === 1 ? 'result' : 'results'}
          </div>
          {products.map((product: any) => {
            const imageUrl = getImageUrl(
              product.images?.find((img: any) => img.isMain)?.thumbnailUrl ||
              product.images?.[0]?.thumbnailUrl ||
              product.images?.find((img: any) => img.isMain)?.url ||
              product.images?.[0]?.url
            );
            const price = product.minPrice ?? product.variants?.[0]?.finalPrice ?? 0;
            const categoryLabel = [product.categoryName, product.subcategoryName].filter(Boolean).join(' / ');
            return (
              <button
                key={product.id}
                onClick={() => onSelect(product.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-50 transition-colors text-left"
              >
                <div className="w-11 h-11 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0">
                  {imageUrl ? (
                    <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300">
                      <Search className="h-4 w-4" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">{product.name}</p>
                  {categoryLabel && (
                    <p className="text-[11px] text-neutral-400 truncate">{categoryLabel}</p>
                  )}
                </div>
                <span className="text-sm text-brand-primary font-semibold flex-shrink-0">${price.toFixed(2)}</span>
              </button>
            );
          })}

          <div className="border-t border-neutral-100">
            {hasMore && (
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={isFetching}
                className="w-full px-3 py-2.5 text-sm text-brand-primary hover:bg-neutral-50 transition-colors flex items-center justify-center gap-2"
              >
                {isFetching ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  `Show more (${totalCount - products.length} remaining)`
                )}
              </button>
            )}
            <button
              onClick={onViewAll}
              className="w-full px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors border-t border-neutral-100"
            >
              View all results →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Navbar({
  categories,
  cartItemCount,
}: NavbarProps) {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hideRow2, setHideRow2] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const lastScrollY = useRef(0);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const mobileSearchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastScrollY.current && currentY > 100) {
        setHideRow2(true);
      } else {
        setHideRow2(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inDesktop = searchContainerRef.current?.contains(target);
      const inMobile = mobileSearchContainerRef.current?.contains(target);
      if (!inDesktop && !inMobile) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoClick = () => {
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowDropdown(false);
    }
  };

  const handleProductSelect = useCallback((productId: string) => {
    navigate(`/product/${productId}`);
    setSearchQuery('');
    setShowDropdown(false);
  }, [navigate]);

  const handleViewAll = useCallback(() => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowDropdown(false);
    }
  }, [navigate, searchQuery]);

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
    setIsMobileMenuOpen(false);
  };

  const handleSubcategoryClick = (categoryId: string, subcategoryId: string) => {
    navigate(`/category/${categoryId}/${subcategoryId}`);
    setIsMobileMenuOpen(false);
  };

  const row1Ref = useRef<HTMLDivElement>(null);
  const [row1Height, setRow1Height] = useState(72);

  useEffect(() => {
    const el = row1Ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setRow1Height(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const row2Hidden = hideRow2 && !isMobileMenuOpen;

  return (
    <>
      {/* Row 1: Fixed, always visible, always on top */}
      <div ref={row1Ref} className="fixed top-0 left-0 right-0 z-navbar bg-white border-b border-neutral-200" role="banner">
        {/* Skip to main content link */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-brand-primary focus:text-white focus:rounded-md"
        >
          Skip to main content
        </a>

        <div className="storefront-container px-4">
          <div className="flex flex-wrap items-center justify-between py-2 gap-x-3 gap-y-1 min-[381px]:flex-nowrap min-[381px]:py-0 min-[381px]:h-navbar sm:gap-4">
            {/* Logo */}
            <button
              onClick={handleLogoClick}
              className="hover:opacity-80 transition-opacity duration-300 flex-shrink-0"
              aria-label="Go to homepage"
            >
              <img 
                src="/thenutshop-logo-x.svg" 
                alt="The Nut Shop" 
                className="h-8 sm:h-9 xl:h-10 w-auto"
              />
            </button>

            {/* Search Bar - sm and up (with dropdown) */}
            <div ref={searchContainerRef} className="hidden sm:block flex-1 max-w-xl order-2 min-[381px]:order-none relative">
              <form onSubmit={handleSearch} className="flex">
                <div className="relative w-full">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => searchQuery.length >= 1 && setShowDropdown(true)}
                    placeholder="Search products..."
                    className="w-full pl-4 pr-12 py-2.5 border border-neutral-300 rounded-full bg-neutral-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm transition-all"
                  />
                  <button
                    type="submit"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-brand-primary text-white rounded-full hover:bg-brand-primary-hover transition-colors"
                    aria-label="Search"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </form>
              <SearchDropdown
                query={searchQuery}
                onSelect={handleProductSelect}
                onViewAll={handleViewAll}
                visible={showDropdown}
              />
            </div>

            {/* Right: Icons */}
            <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0 order-1 min-[381px]:order-none ml-auto min-[381px]:ml-0">
              {isAuthenticated && <NotificationDropdown />}
              <LoginIndicator />
              <div className="ml-1">
                <CartIndicator itemCount={cartItemCount} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Slides under row 1 when hidden */}
      <nav
        className={`fixed left-0 right-0 bg-neutral-50 shadow-sm z-[999] transition-transform duration-300 ease-in-out ${
          row2Hidden ? '-translate-y-full' : 'translate-y-0'
        }`}
        style={{ top: `${row1Height}px` }}
        aria-label="Main navigation"
      >
        <div className="storefront-container px-4">
          {/* Desktop: Centered categories */}
          <div className="hidden xl:flex items-center justify-center gap-1 py-2">
            {categories.map((category) => (
              <CategoryDropdown key={category.id} category={category} />
            ))}
          </div>

          {/* Mobile/Tablet: Categories button + Search (mobile only) */}
          <div className="xl:hidden flex items-center gap-3 py-2.5">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 rounded-full hover:bg-neutral-100 transition-colors flex-shrink-0"
              aria-label={isMobileMenuOpen ? 'Close categories' : 'Open categories'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-4 w-4 text-neutral-700" />
              ) : (
                <Menu className="h-4 w-4 text-neutral-700" />
              )}
              <span className="text-sm font-medium text-neutral-700">Categories</span>
            </button>

            {/* Search - Mobile only (< sm) with dropdown */}
            <div ref={mobileSearchContainerRef} className="flex-1 sm:hidden relative">
              <form onSubmit={handleSearch}>
                <div className="relative w-full">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => searchQuery.length >= 1 && setShowDropdown(true)}
                    placeholder="Search products..."
                    className="w-full pl-4 pr-12 py-2.5 border border-neutral-300 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm transition-all"
                  />
                  <button
                    type="submit"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-brand-primary text-white rounded-full hover:bg-brand-primary-hover transition-colors"
                    aria-label="Search"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </form>
              <SearchDropdown
                query={searchQuery}
                onSelect={handleProductSelect}
                onViewAll={handleViewAll}
                visible={showDropdown}
              />
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="xl:hidden border-t border-neutral-200 py-3 animate-slide-down max-h-[calc(100vh-10rem)] overflow-y-auto bg-white shadow-lg">
            <div className="storefront-container px-4">
              <div className="flex flex-col gap-0.5">
                {categories.map((category) => (
                  <div key={category.id} className="flex flex-col">
                    <button
                      onClick={() => handleCategoryClick(category.id)}
                      className="text-left px-4 py-3 text-sm font-medium text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
                    >
                      {category.name}
                    </button>
                    {category.subcategories && category.subcategories.length > 0 && (
                      <div className="ml-4 flex flex-col">
                        {category.subcategories.map((subcategory) => (
                          <button
                            key={subcategory.id}
                            onClick={() => handleSubcategoryClick(category.id, subcategory.id)}
                            className="text-left px-4 py-2.5 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 rounded-lg transition-colors"
                          >
                            {subcategory.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
